import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Briefcase, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";

// ── Validation helper ──────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validateEmail(v) {
  if (!v) return "Email is required";
  if (!EMAIL_RE.test(v)) return "Enter a valid email address";
  return "";
}

export default function Login() {
  const { login, formatError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);

  const emailError = validateEmail(email);

  const submit = async (e) => {
    e.preventDefault();
    setTouchedEmail(true);
    if (emailError) return;
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      if (u.role === "super_admin") navigate("/superadmin");
      else if (u.role === "admin") navigate("/admin");
      else if (u.role === "job_poster") navigate("/dashboard/poster");
      else navigate("/dashboard/seeker");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Welcome back</h1>
        <p className="text-slate-500 text-sm mt-1">Sign in to post or apply for jobs.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          {/* Email */}
          <div>
            <Label>Email</Label>
            <div className="relative mt-1">
              <Input
                type="text"
                inputMode="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value.trim())}
                onBlur={() => setTouchedEmail(true)}
                className={`pr-8 border transition ${
                  touchedEmail && emailError
                    ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                    : touchedEmail && !emailError
                    ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                    : "border-slate-300 focus:border-black focus:ring-black"
                }`}
                data-testid="login-email-input"
              />
              {touchedEmail && (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                  {emailError
                    ? <XCircle className="w-4 h-4 text-red-500" />
                    : <CheckCircle2 className="w-4 h-4 text-green-500" />
                  }
                </span>
              )}
            </div>
            {touchedEmail && emailError && (
              <p className="flex items-center gap-1 text-xs text-red-600 mt-1">
                <XCircle className="w-3 h-3 shrink-0" />{emailError}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <Label>Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                className="border-slate-300 focus:border-black focus:ring-black pr-9"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-black"
                data-testid="login-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full bg-[#000000] hover:bg-slate-800 text-white" data-testid="login-submit-btn">
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-slate-200"></div>
          <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-slate-200"></div>
        </div>
        <GoogleLoginButton role="job_seeker" />
        <p className="text-sm text-slate-500 mt-6 text-center">
          New to Job Dew? <Link to="/register" className="text-[#000000] font-medium">Create an account</Link>
        </p>
      </div>
    </div>
  );
}